import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import LoginHeaderOperational from '../operational-model/LoginHeaderOperational';
import { API_URL } from '../../constant/API_Settings';
import {
  authHeaders,
  inboxGeneratedMeta,
  INBOX_HOVER_READ_MS,
  isInboxUnread,
  markReportNotificationRead,
  notifyReportDownloadsRefresh,
  REPORT_DOWNLOADS_REFRESH_EVENT,
} from '../../utils/reportFormat';
import './report-downloads.css';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'read', label: 'Read' },
];

const EMPTY_SUMMARY = {
  unread: 0,
  read: 0,
  total: 0,
};

/**
 * Full bell inbox: formatted time, who generated, unread red mark, hover to mark read.
 */
const AllNotification = (props) => {
  const { id } = useParams();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [filter, setFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const hoverTimers = useRef({});

  useEffect(() => {
    if (String(id) === '2') {
      history.replace('/report-downloads');
    }
  }, [id, history]);

  const load = useCallback((nextPage, nextFilter, nextSearch) => {
    setLoading(true);
    const params = new URLSearchParams({
      n_id: '1',
      filter: nextFilter,
      search: nextSearch,
      page: String(nextPage),
      pagination_limit: '15',
    });

    fetch(`${API_URL}/allnotification?${params.toString()}`, {
      method: 'get',
      headers: authHeaders(),
    })
      .then((response) => response.json())
      .then((data) => {
        setItems(data.data || []);
        setSummary(data.summary || EMPTY_SUMMARY);
        setPage(data.current_page || 1);
        setLastPage(data.last_page || 1);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (String(id) === '2') {
      return undefined;
    }
    load(page, filter, search);
    const onRefresh = () => load(page, filter, search);
    window.addEventListener(REPORT_DOWNLOADS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(REPORT_DOWNLOADS_REFRESH_EVENT, onRefresh);
  }, [filter, page, search, load, id]);

  const clearHover = (itemId) => {
    if (hoverTimers.current[itemId]) {
      clearTimeout(hoverTimers.current[itemId]);
      delete hoverTimers.current[itemId];
    }
  };

  const markRead = async (item) => {
    if (!item?.id || !isInboxUnread(item)) {
      return;
    }
    setItems((prev) => {
      const next = prev.map((row) => (row.id === item.id ? { ...row, status: 2 } : row));
      return filter === 'unread' ? next.filter((row) => isInboxUnread(row)) : next;
    });
    setSummary((prev) => ({
      ...prev,
      unread: Math.max(0, (Number(prev.unread) || 0) - 1),
      read: (Number(prev.read) || 0) + 1,
    }));
    await markReportNotificationRead(item.id);
    notifyReportDownloadsRefresh();
  };

  const handleMouseEnter = (item) => {
    if (!isInboxUnread(item)) {
      return;
    }
    clearHover(item.id);
    hoverTimers.current[item.id] = setTimeout(() => {
      markRead(item);
    }, INBOX_HOVER_READ_MS);
  };

  const openItem = async (item) => {
    await markRead(item);
    if (item.url) {
      window.open(item.url, '_blank', 'noopener');
    }
  };

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={8} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Notifications" />
          <div className="container pb-3">
            <div className="rd-page">
              <section className="rd-hero nt-hero">
                <div className="rd-hero-badge">Inbox</div>
                <h1>Your notifications</h1>
                <p>
                  Unread items show a red mark. Hover a card to mark it read, the same way as the header bell.
                  Date and who generated it are shown on every row.
                </p>
              </section>

              <div className="rd-stats nt-stats">
                <div className="rd-stat nt-stat-unread">
                  <strong>{summary.unread || 0}</strong>
                  <span>Unread</span>
                </div>
                <div className="rd-stat nt-stat-read">
                  <strong>{summary.read || 0}</strong>
                  <span>Read</span>
                </div>
                <div className="rd-stat nt-stat-total">
                  <strong>{summary.total || 0}</strong>
                  <span>Total</span>
                </div>
              </div>

              <div className="rd-toolbar">
                <div className="rd-filters">
                  {FILTERS.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className={`rd-filter ${filter === entry.id ? 'is-active' : ''}`}
                      onClick={() => {
                        setFilter(entry.id);
                        setPage(1);
                      }}
                    >
                      {entry.label}
                    </button>
                  ))}
                </div>
                <form
                  className="rd-search"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setPage(1);
                    setSearch(searchInput);
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search title or note"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                  <button type="submit">Search</button>
                </form>
              </div>

              {loading && items.length === 0 ? (
                <div className="rd-empty">Loading notifications…</div>
              ) : items.length === 0 ? (
                <div className="rd-empty">No notifications in this view.</div>
              ) : (
                <div className="rd-card-list">
                  {items.map((item) => {
                    const unread = isInboxUnread(item);
                    const meta = inboxGeneratedMeta(item);
                    return (
                      <article
                        key={item.id}
                        className={`rd-card nt-card ${unread ? 'is-unread' : ''}`}
                        onMouseEnter={() => handleMouseEnter(item)}
                        onMouseLeave={() => clearHover(item.id)}
                      >
                        <div className="nt-card-main">
                          <h2 className="rd-card-title">{item.title || 'Notification'}</h2>
                          <p className="rd-card-note">{item.note}</p>
                          <span className={`rd-pill ${unread ? 'rd-pill-unread-red' : 'rd-pill-read'}`}>
                            {unread ? 'Unread' : 'Read'}
                          </span>
                          <div className="rd-card-meta">
                            {meta.when ? <span>{meta.when}</span> : null}
                            {meta.who ? <span>Generated by {meta.who}</span> : null}
                          </div>
                        </div>
                        <div className="rd-card-actions">
                          {unread ? <span className="inbox-unread-dot nt-card-dot" title="Unread" /> : null}
                          {item.url ? (
                            <button type="button" className="rd-download-btn" onClick={() => openItem(item)}>
                              Open
                            </button>
                          ) : unread ? (
                            <button type="button" className="rd-ghost-btn" onClick={() => markRead(item)}>
                              Mark read
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {lastPage > 1 ? (
                <div className="rd-pager">
                  <button type="button" className="rd-ghost-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                  <span>Page {page} of {lastPage}</span>
                  <button type="button" className="rd-ghost-btn" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllNotification;
